/**
 * @module utils/utils
 */
import tgamConfig from "config/tgam";
import isNil from "lodash/isNil";
import isFunction from "lodash/isFunction";
import get from "lodash/get";
import has from "lodash/has";
import set from "lodash/set";
import ee from "lib/eventemitter";
import logger from "utils/logger";

const debugTag = "[UTILS]";
/**
 * @method isBooleanString
 * @description whether string can be converted to a boolean
 * @param {String} val
 * @returns {Boolean}
 */
export function isBooleanString(val) {
  return typeof val === "string" && (val.trim() === "true" || val.trim() === "false");
}

/**
 * @method isArrayString
 * @description whether string can be converted to an array
 * @param {String} val
 * @returns {Boolean}
 */
export function isArrayString(val) {
  return typeof val === "string" && (val.trim().charAt(0) === "[" && val.trim().charAt(val.length - 1) === "]");
}

/**
 * @method hasUniqueValues
 * @description whether the values in the array are unique
 * @param {Array} arr - of values
 * @returns {Boolean} true - if values are unique
 */
export function hasUniqueValues(arr) {
  return arr.length === new Set(arr).size;
}

/**
 * @method isNumberString
 * @description whether string can be converted to a number
 * @param {String} val
 * @returns {Boolean}
 */
export function isNumberString(val) {
  return typeof val === "string" && !isNaN(val);
}

/**
 * @method stringToArray
 * @description converts a string to array
 * @param {String} val
 * @returns {Array<String>}
 */
export function stringToArray(val) {
  return val.replace(/[\[\]']+/g, "").split(",").map((el) => el.trim());
}

/**
 * @method toArray
 * @description converts an array like (NodeList) to an array.
 * @param {NodeList|HTMLCollection} obj
 * @returns {Array}
 */
export const toArray = (() => Array.from ? Array.from : obj => [].slice.call(obj))();

/**
 * @method getById
 * @description Returns the first element that is a descendant of the element on that matches the id value.
 * @param {String} identity - the desired element's id attribute value
 * @returns {HTMLElement} of first element found which matches the group of selectors.
 */
export function getById(identity) {
  return window.document.getElementById(identity);
}

/**
 * @method qs
 * @description Returns the first element that is a descendant of the element on that matches the group of selectors.
 * @param {String} selectors - A group of selectors to match the descendant elements of the baseElement against.
 * This must be valid CSS syntax, or a SyntaxError exception will occur
 * @param {Document|HTMLElement} [baseElement=window.document] - element/object to perform the query upon.
 * @returns {HTMLElement} of first element found which matches the group of selectors.
 */
export function qs(selectors, baseElement = window.document) {
  return baseElement.querySelector(selectors);
}

/**
 * @method qsa
 * @description Returns an Array representing a (NodeList) list of elements matching the specified group of selectors.
 * @param {String} selectors - A group of selectors to match the descendant elements of the baseElement against.
 * This must be valid CSS syntax, or a SyntaxError exception will occur
 * @param {Document|HTMLElement} [baseElement=window.document] - element/object to perform the query upon.
 * @returns {Array} of all elements found which matches the group of selectors.
 */
export function qsa(selectors, baseElement = window.document) {
  return toArray(baseElement.querySelectorAll(selectors));
};

/**
 * @method removeInnerQuotes
 * @description removed inner quotes from a string
 * @param {String} val
 * @returns {String}
 */
export function removeInnerQuotes(val) {
  return typeof val === "string" ? val.replace(/^['|"]|['|"]$/g, "") : val;
}

/**
 * @method hasNativeDataType
 * @description whether a string can be converted to another data type other than a string
 * @param {String} val
 * @returns {Boolean}
 */
export function hasNativeDataType(val) {
  return isBooleanString(val) || isArrayString(val) || isNumberString(val);
}

/**
 * @method hasValue
 * @description Checks if `value` is not `null` or `undefined` and not an empty string.
 * @param {String} val - the value to check.
 * @returns {Boolean} `true` if `value` is not nullish and not empty
 */
export function hasValue(val) {
  return !isNil(val) && val.trim() !== "";
}

/**
 * @method normalizeObjectStrings
 * @description takes an input object and normalizing property values to lowercase strings
 *    NOTE: this does not recursively loop through sub objects
 * @param {Object} data - input object which will loop through each property and if the value is a string, it will make it lowercase
 * @param {Boolean} [changeToLowercase=true] - whether or not to change to lowercase
 * @returns {Object}
 */
export function normalizeObjectStrings(data, changeToLowercase = true) {
  const keys = Object.keys(data);
  return keys.reduce((accumulator, propertyName) => {
    let obj = accumulator;
    let dataValue = data[propertyName];
    // TODO: If we start filtering more, we should consider filtering through an "allowed" function
    if (typeof dataValue === "string" && propertyName !== "contentId") {
      obj[propertyName] = changeToLowercase ? dataValue.toLowerCase() : dataValue.toUpperCase();
    } else {
      obj[propertyName] = dataValue;
    }
    return obj;
  }, {});
}

/**
 * @method removeDirtyChars
 * @description Remove whitespace characters from a string
 * @param {String} str - the dirty value to be cleaned
 * @returns {String}
 */
export function removeDirtyChars(str) {
  return str.replace(/\n|\r|\t|\0/gim, "");
}

/**
 * @method getProp
 * @description Safely access deeply nested value in JS object.
 * @param {Object} obj - The object to query.
 * @param {Array|String} prop - The path of the property to get. As an array or string delimited with "."
 * @param {*} [def=null] - The value returned for undefined resolved values.
 * @returns {*} of resolved value, or default if path is not found.
 */
export function getProp(obj, prop, def = null) {
  return get(obj, prop, def); // https://lodash.com/docs/#get
}

/**
 * @method hasProp
 * @description Safely check deeply nested value in JS object.
 * @param {Object} obj - The object to query.
 * @param {Array|String} prop - The path of the property to get. As an array or string delimited with "."
 * @returns {Boolean} true if the property exists
 */
export function hasProp(obj, prop) {
  return has(obj, prop); // https://lodash.com/docs/#has
}

/**
 * @method setProp
 * @description Safely set deeply nested value in JS object. This method mutates object.
 * @param {Object} obj - The object to modify.
 * @param {Array|String} prop - The path of the property to get. As an array or string delimited with "."
 * @param {*} [val=null] - The value to set.
 * @returns {Object} of updated object, or the same object if val is undefined.
 */
export function setProp(obj, prop, val = null) {
  return set(obj, prop, val); // https://lodash.com/docs/#set
}

/**
 * @method validateEmail
 * @description Validate the email address according to regex pattern.
 * Pattern taken from: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email
 * @param {String} email - of the subscriber.
 * @returns {Boolean} true if email address is valid.
 */
export function validateEmail(email) {
  if (isNil(email)) {
    return false;
  }
  const re = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return re.test(email.toLowerCase());
}

/**
 * @method isFunctionThenInvoke
 * @description If `func` is a Function, then return the value of invoked `func` with `args`.
 * Otherwise, if `func` isn't a Function, the return the `def` (default) value.
 * @param {Function} func - The function to invoke.
 * @param {Array} [args=null] - The arguments to invoke `func` with.
 * @param {*} [def=null] - The default value to return.
 * @returns {*} of result from `func`.
 */
export function isFunctionThenInvoke(func, args = null, def = null) {
  return isFunction(func) ? func.apply(undefined, args) : def; // https://lodash.com/docs/#isFunction
}

/**
 * @method relativeUrl
 * @description Ensures homepage URL is correct for environment.
 * @param {String} [url="/"]
 * @returns {String}
 */
export function relativeUrl(url = "/") {
  let ret = url;
  if (tgamConfig.env.isLocal && url == "/") {
    ret = tgamConfig.env.baseHomepageUrl;
  } else {
    ret = url;
  }
  // don't really need additional trailing slash logic
  return ret;
}

/**
 * @method addParamToUrl
 * @description Adds paramName with paramValue to the URL
 * @param {String} url - URL to modify
 * @param {String} paramValue - query string parameter value to add to URL
 * @param {String} [paramName="intcmp"] - query string parameter name to add to URL
 * @returns {String} The original URL or a transformed version of it
 */
export function addParamToUrl(url, paramValue, paramName = "intcmp") {
  let finalUrl = url;
  // If the URL points to the subscription funnel (i.e. "subscribe.theglobeandmail") then
  // it must follow the non-standard "#/fragment?param=value" format. Otherwise, it should
  // follow the standard "?param=value#/fragment" format. See "lib/consts.js" for details.
  if (!url.includes("subscribe.theglobeandmail.com") && url.includes("#")) {
    finalUrl = url.replace("#", `?${paramName}=${paramValue}#`);
  } else if (url.includes("?")) {
    finalUrl = `${url}&${paramName}=${paramValue}`;
  } else {
    finalUrl = `${url}?${paramName}=${paramValue}`;
  }
  return finalUrl;
}

/**
 * @method windowOnLoad
 * @description Method is mostly to accommodate IE 11 support.
 * @param {Function} func - method to invoke when window load event fires
 * @param {Object} [w=window] - the browser window object (optional)
 */
export function windowOnLoad(func, w = window) {
  // If the load event has already fired, then execute the function immediately
  if (document.readyState === "complete") {
    func();
    return;
  }
  // Detect IE 10 or IE 11. Even though IE supports addEventListener(),
  // the "load" event will always fail to fire.
  if (w.navigator.userAgent.indexOf("MSIE ") > 0 || w.navigator.userAgent.indexOf("Trident/") > 0) {
    func();
  } else if (w.addEventListener) {
    // modern browsers
    w.addEventListener("load", func, false);
  } else if (w.attachEvent) {
    // less modern
    w.attachEvent("onload", func);
  } else {
    func();
  }
}

/**
 * @method vendorPrefix
 * @description Determine if the browser supports a specific CSS property.
 * If not, then determine which vendor prefix to apply for this browser.
 * Browser vendors checked are: Webkit, Mozilla, Microsoft, and Opera.
 * @param {String} property - The desired property to test.
 * @returns {String} of browser safe property value.
 */
export function vendorPrefix(property) {
  let div = document.createElement("div");
  let prop = property.toLowerCase();
  if (isNil(div.style[prop])) {
    let vendors = ["webkit", "moz", "ms", "o"];
    for (let v in vendors) {
      if (!isNil(div.style[vendors[v] + property])) {
        return vendors[v] + property;
      }
    }
  }
  return prop;
}

/**
 * @method parseDataSetObject
 * @description Convert the element dataset attribute value into JSON
 * @param {String} attr - The desired attribute string to be processed.
 * @returns {Object} of parsed value
 */
export function parseDataSetObject(attr) {
  if (attr) {
    try {
      return JSON.parse(attr);
    } catch (e) {
      console.error("JSON.parse failed to parse attr:", attr, e);
      return {};
    }
  }
}

/**
 * @method toggleAttribute
 * @description Toggle a boolean attribute (removing it if it's present or adding it if it's not).
 * @param {HTMLElement} el - element to toggle attribute on.
 * @param {String} attr - attribute to toggle.
 * @returns {HTMLElement}
 */
export function toggleAttribute(el, attr) {
  if (el.hasAttribute(attr)) {
    el.removeAttribute(attr);
  } else {
    el.setAttribute(attr, attr);
  }
  return el;
}

/**
 * @method getAttrValue
 * @description Returns attribute value from an HTML string.
 * @param {String} str - HTML string for which the attribute value is to be found.
 * @param {String} attribute - name of attribute
 * @param {String} pattern - regular expression that matches attribute's value.
 * @returns {String} value of attribute
 */
export function getDataAttrValFromStr(str, attribute, pattern) {
  const regex = new RegExp(`${attribute}="${pattern}"`, "g");
  const match = regex.exec(str);

  return match.length >= 2 ? match[1] : "";
}

/**
 * @method removeNode
 * @description Removes the passed node or array of nodes, from the DOM.
 * @param {HTMLElement|Array<HTMLElement>} node - to be removed
 */
export function removeNode(node) {
  const remove = n => {
    if (n && n.parentNode) {
      n.parentNode.removeChild(n);
    }
  };
  if (Array.isArray(node)) {
    node.forEach(remove);
  } else {
    remove(node);
  }
}

/**
 * @method checkEventListenerOptions
 * @description Determine browser support for the various `addEventListener` options param.
 * @see https://www.audero.it/blog/2018/04/18/in-depth-guide-event-listeners/
 * @returns {{ capture: Boolean, passive: Boolean, once: Boolean }}
 */
function supportEventListenerOptions() {
  const optionsAvailable = {
    capture: false,
    passive: false,
    once: false
  };

  try {
    const eventListenerOptions = {};
    Object
      .keys(optionsAvailable)
      .forEach(option => {
        Object.defineProperty(eventListenerOptions, option, {
          get() {
            optionsAvailable[option] = true;
          }
        });
      });

    window.addEventListener("test", null, eventListenerOptions);
  } catch (ex) {
    // nothing
  } finally {
    return optionsAvailable;
  }
}
export const eventListenerOptions = supportEventListenerOptions();

/**
 * @method supportsFocusWithin
 * @description Determine browser support for :focus-within (copied from Modernizr)
 * @see https://github.com/Modernizr/Modernizr/blob/master/feature-detects/css/focuswithin.js
 * @returns {Boolean}
 */
export function supportsFocusWithin() {
  try {
    document.querySelector(":focus-within");
  } catch (error) {
    return false;
  }
  return true;
}

/**
 * @method interceptEventCallback
 * @description Bind an `click` event on the element, and call the callback method when action happens.
 * @param {Element} element - to bind the action event onto.
 * @param {String} event - of event to be bound.
 * @param {Function} callback - called upon click.
 */
export function interceptEventCallback(element, event, callback) {
  if (isNil(element)) {
    return;
  }

  // Older browsers don't support these options. e.g. IE 11
  if (!eventListenerOptions.capture
    || !eventListenerOptions.passive
    || !eventListenerOptions.once) {
    callback(); // so fallback to calling method immediately
    return;
  }

  // Delegate the click event listener to the root element, and intercept that action.
  // The click event should then bubble up to the child element, after "callback" is completed.
  element.addEventListener(event, callback, {
    capture: true,
    passive: true,
    once: true
  });
}

/**
 * @method promisifyEvent
 * @summary Convert an event-emitter "once" event into a Promise that resolves once.
 * @description Here are some guidelines for usage:
 * 1. You can use this method to convert an event into a Promise, since there is no way to
 *    share a Promise across Webpack bundles. There is no global "Promise repository", similar
 *    to the global `ee` event-emitter.
 * 2. If you're calling a method which performs an async operation, then by default you should
 *    return a Promise from that method. You're probably calling that method directly from within
 *    the same WebPack (JS) bundle. Use the value resolved from that Promise, instead of this method.
 * 3. But, if you're interested in the resolved value from that method's async operation, from a
 *    different WebPack (JS) bundle that called the method. Then you can use the `ee`
 *    (event-emitter) to broadcast an event, from within the method, when that operation
 *    is complete. Events are capable of passing data as params.
 * 4. Try to restrict use to when you have multiple events which you need to chain in a
 *    Promise sequence, or when you need to use Promise.race() or Promise.all().
 * @param {String} name - of the event to watch for.
 * @returns {Promise<Object>}
 */
export function promisifyEvent(name) {
  return new Promise(resolve => {
    ee.once(name, data => {
      resolve(data);
    });
  });
}

/*
 * Returns age (from birthday to toDate).
 * @param {Number} birthYear - year of birth
 * @param {Number} birthMonth - month of birth
 * @param {Number} birthDay - day of birth
 * @param {Date} toDate - date to which age is to be calculated - defaults to current date
 * @returns {Number}
 */
export function calculateAge(birthYear, birthMonth, birthDay, toDate = new Date()) {
  const toYear = toDate.getFullYear();
  const toMonth = toDate.getMonth() + 1; // Date's getMonth() starts at index 0 so adjust for that
  const toDay = toDate.getDate();

  const diffYear = toYear - birthYear;
  const diffMonth = toMonth - birthMonth;
  const diffDay = toDay - birthDay;

  let age = diffYear;

  logger.groupCollapsed(`${debugTag} calculateAge`);
  logger.info(`${debugTag} birth`, { birthYear, birthMonth, birthDay });
  logger.info(`${debugTag} to`, { toYear, toMonth, toDay });
  logger.info(`${debugTag} diff`, {
    year: `${toYear} - ${birthYear} = ${diffYear}`,
    month: `${toMonth} - ${birthMonth} = ${diffMonth}`,
    day: `${toDay} - ${birthDay} = ${diffDay}`
  });

  if (diffMonth < 0 || (diffMonth == 0 && diffDay < 0)) { // Birthday has not occurred yet for the year
    logger.info(`${debugTag} birthday has not occurred yet`);
    age = age - 1;
  }
  logger.info(`${debugTag} age`, age);
  logger.groupEnd(`${debugTag} calculateAge`);

  return age;
}

/**
 * Returns whether or not `day`, is a valid day.
 * @param {String} day - day to be validated
 * @returns {Boolean}
 */
export function isValidDay(day) {
  let convertedDay = Number(day);
  return Number.isInteger(convertedDay) && convertedDay >= 1 && convertedDay <= 31;
}

/**
 * Returns whether or not `month`, is a valid month.
 * @param {String} month - month to be validated
 * @returns {Boolean}
 */
export function isValidMonth(month) {
  let convertedMonth = Number(month);
  return Number.isInteger(convertedMonth) && convertedMonth >= 1 && convertedMonth <= 12;
}

/**
 * Returns whether or not `year`, is a valid year.
 * @param {String} year - year to be validated.
 * @param {Number} [minYear] - minimum year allowed as valid
 * @param {Number} [maxYear] - maximum year allowed as valid
 * @returns {Boolean}
 */
export function isValidYear(year, minYear = 1900, maxYear = new Date().getFullYear()) {
  let convertedYear = Number(year);
  return Number.isInteger(convertedYear) && convertedYear >= minYear && convertedYear <= maxYear;
}

/**
 * Determine if the current browser is of the Internet Explorer family
 */
export const notIE = !(window.navigator.userAgent.indexOf("MSIE ") > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./));

/**
 * Determine if the current browser is IE 11.
 */
export const isIE11 = Object.hasOwnProperty.call(window, "ActiveXObject") && !window.ActiveXObject;

/**
 * Determine if the current browser is Jest test environment.
 */
export const isJest = ["test", "ci"].includes(getProp(window, "process.env.NODE_ENV", ""));

/**
 * @method areChronologicalEpochs
 * @description Checks to see if middle date epoch is within start and end range.
 * If start and end ranges aren't supplied, then return false.
 * @param {Number} start - epoch of start range
 * @param {Number} middle - epoch of middle or "now" (to ensure it's within range)
 * @param {Number} end - epoch of end range
 * @param {Boolean} def - default value if invalid params
 * @returns {Boolean} true if in order
 */
export function areChronologicalEpochs(start, middle, end, def = false) {
  if (isNaN(start) || isNaN(middle) || isNaN(end)) {
    return def;
  }
  if (start === 0 || end === 0) {
    return def;
  }
  return middle >= start && middle <= end;
}

/**
 * @method formatEpoch
 * @description Format a numerical epoch value as a human readable date string.
 * @see https://www.epochconverter.com/
 * @param {Number} [epoch=0] - Unix Timestamp
 * @returns {String}
 */
export function formatEpoch(epoch = 0) {
  let d = new Date(0);
  d.setUTCSeconds(epoch);
  return d.toUTCString();
}

/**
 * @method showCookieAlert
 * @description Determines if the cookies alert should be shown.
 * @returns {Boolean}
 */
export function showCookieAlert() {
  const cookiesEnabled = getProp(window, "tgam.cookiesEnabled");
  const cookieAlertEnabled = tgamConfig.env.cookieAlertEnabled === "true" ? true : false;
  return !cookiesEnabled && cookieAlertEnabled;
}

/**
 * Determines if first party cookies enabled
 * @returns {Boolean}
 * @see https://github.com/Modernizr/Modernizr/blob/master/feature-detects/cookies.js
 */
export function checkFirstPartyCookies() {
  try {
    document.cookie = "cookietest=1";
    const val = document.cookie.indexOf("cookietest=") !== -1;
    document.cookie = "cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT";
    return val;
  } catch (e) {
    return false;
  }
}

/**
 * @description will generate a random string of 4 or 5 characters, always different. Example output is "30jzm", "1r591" or "4su1a"
 * @see https://stackoverflow.com/questions/1349404/generate-random-string-characters-in-javascript#answer-28997977
 * @returns {String}
 */
export function cacheToken() {
  return (0 | Math.random() * 9e6).toString(36);
}

/**
 * Returns whether or not item associated with startPropName and endPropName
 * is disabled based on supplied Epoch time values.
 * @param {Object} config - of tgam vars
 * @param {Number} now - to compare against the start and end range
 * @param {String} startPropName - property name of item that contains Epoch start time
 * @param {String} endPropName - property name of item that contains Epoch end time
 * @returns {Boolean} true when item is disabled
 */
export function isTimedDisabled(config, now, startPropName, endPropName) {
  const start = Number(getProp(config, startPropName, 0));
  const end = Number(getProp(config, endPropName, 0));
  const disabled = areChronologicalEpochs(start, now, end, false);

  logger.groupCollapsed(`${debugTag} timers`);
  logger.info({
    start: `${start} - ${formatEpoch(start)}`,
    now: `${now} - ${formatEpoch(now)}`,
    end: `${end} - ${formatEpoch(end)}`,
    within: disabled // true when within the datetime range
  });
  logger.groupEnd(`${debugTag} timers`);

  return disabled;
}

/**
 * Merge object which contains properties that are Arrays.
 * Meant to be used by Lodash's `mergeWith()` as the customizer param.
 * Note: This method mutates object.
 * @see https://devdocs.io/lodash~4/index#mergeWith
 * @param {Object} objValue - to be concatenated onto
 * @param {Object} srcValue - to be concatenated with
 * @returns {Object} merged object
 */
export function arrayMerge(objValue, srcValue) {
  if (Array.isArray(objValue)) {
    return objValue.concat(srcValue);
  }
}
